import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"
import { useEffect, useRef, useState } from "react"


import { Horizontal } from "@/components/Flex.ts"
import { MarkdownWrapper } from "@/components/Markdown/MarkdownWrapper.tsx"

import type { DMDecoratedText } from "@/lib/client/generated/TypeDefinitions.ts"

const ANIMATION_DURATION_MS = 200

type Phase = "entering" | "visible" | "exiting"

export const EnvironmentLoadingText = observer((props: {
  customText?: DMDecoratedText | string | null
}) => {
  const [phase, setPhase] = useState<Phase>("entering")
  const [displayText, setDisplayText] = useState<DMDecoratedText | string | null>(props.customText || null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (props.customText !== displayText) {
      setPhase("exiting")

      timeoutRef.current = setTimeout(() => {
        setDisplayText(props.customText || null)
        setPhase("entering")

        timeoutRef.current = setTimeout(() => {
          setPhase("visible")

          const delay = Math.random() * 4000 + 2000
          timeoutRef.current = setTimeout(() => {
            if (props.customText === displayText) {
              setPhase("exiting")
            }
          }, delay)
        }, ANIMATION_DURATION_MS)
      }, ANIMATION_DURATION_MS)
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [props.customText, displayText])

  useEffect(() => {
    if (displayText) {
      timeoutRef.current = setTimeout(() => setPhase("visible"), ANIMATION_DURATION_MS)

      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
      }
    }
  }, [displayText])

  if (!displayText) return null

  return (
    <Container>
      <AnimatedText phase={phase}>
        {typeof displayText === "string" ? displayText : <MarkdownWrapper text={displayText} />}
      </AnimatedText>
    </Container>
  )
})

const Container = styled(Horizontal)`
  align-items: center;
  justify-content: center;

  padding: 0 16px;
  text-align: center;
`

const opacity = (phase: Phase) => {
  switch (phase) {
    case "entering":
      return 0
    case "visible":
      return 1
    case "exiting":
      return 0
  }
}

const translateY = (phase: Phase) => {
  switch (phase) {
    case "entering":
      return "-20px"
    case "visible":
      return "0"
    case "exiting":
      return "20px"
  }
}

const AnimatedText = styled.span<{ phase: Phase }>`
  font-size: 16px;

  color: var(--text-color-40);
  text-shadow: 0 4px 10px var(--background-color-10);
  text-align: center;

  opacity: ${props => opacity(props.phase)};
  transform: translateY(${props => translateY(props.phase)});
  transition: all 200ms ${props => props.phase === "exiting" ? "ease-in" : "ease-out"};
`
